'use client';

import { useMemo } from 'react';
import * as rrd from 'react-router-dom';

import { ConversionParamsProvider } from '@/providers';
import { TABS, PLATFORMS, PLATFORM_CAPTIONS } from '@/constants';
import { convertMeta, slugToPlatform, getConversionMeta } from '@/utils';

import { Meta } from '../segments';

import Converter from './Converter';

const { Navigate, useParams } = rrd.default ?? rrd;

const VALID = new Set(Object.values(PLATFORMS));

const toCaption = (platform) => PLATFORM_CAPTIONS[platform] || platform;

const parseParams = (conversion) => {
  if (!conversion || TABS.includes(conversion.toLowerCase())) {
    return null;
  }

  const match = conversion
    .toLowerCase()
    .match(/^([a-z0-9-]+)-to-([a-z0-9-]+)$/);

  if (!match) return undefined;

  const [, rawSource, rawTarget] = match;

  const source = slugToPlatform(rawSource);
  const target = slugToPlatform(rawTarget);

  if (!VALID.has(source) || !VALID.has(target) || source === target) {
    return undefined;
  }

  return { source, target };
};

const Convert = () => {
  const { conversion } = useParams();

  // If first param is a tab, we are in custom converter mode
  const isTabInFirstSlot = TABS.includes(conversion?.toLowerCase());

  const params = useMemo(() => parseParams(conversion), [conversion]);

  // Only redirect if there is a string that isn't a tab AND isn't a valid pair
  if (conversion && !isTabInFirstSlot && params === undefined) {
    return <Navigate to="/convert" replace />;
  }

  let meta, heading;

  if (params) {
    const { source, target } = params;
    meta = getConversionMeta({ source, target });
    heading = `Transfer ${toCaption(source)} to ${toCaption(target)}`;
  } else {
    meta = convertMeta;
    heading = 'Transfer Music Playlists';
  }

  return (
    <ConversionParamsProvider {...(params ?? {})}>
      <Meta {...meta} />

      <main
        className="container py-4"
        style={{ minHeight: 'calc(100vh - 410px)' }}
      >
        <div className="text-center">
          <h1 className="mb-3">{heading}</h1>
          <p className="text-muted mb-3">
            Choose how your playlists are transferred
          </p>
        </div>

        <Converter />
      </main>
    </ConversionParamsProvider>
  );
};

export default Convert;
